import type {
  PolygonPadConfig,
  PolygonAxisConstraint,
  PolygonPointConstraint,
} from '@shared/types';

export type PolygonPadParseResult = {
  type: 'polygonpad';
  value: number[];
  polygonPadConfig: PolygonPadConfig;
};

/**
 * Parse a polygon constraint token into a PolygonAxisConstraint.
 *
 * Accepted forms:
 *   `!value`          → `{ kind: 'fixed', value }`
 *   `min:max`         → `{ kind: 'range', min, max }`
 *   `min:step:max`    → `{ kind: 'range', min, step, max }`
 *   `number`          → `{ kind: 'free', defaultValue: number }`
 *
 * Returns null if the token is not a valid constraint.
 */
export function parsePolygonAxisConstraint(
  token: string,
): PolygonAxisConstraint | null {
  const t = token.trim();

  // Fixed: !value
  const fixedMatch = t.match(/^!(-?\d+(?:\.\d+)?)$/);
  if (fixedMatch) {
    return { kind: 'fixed', value: Number(fixedMatch[1]) };
  }

  // Range with step: min:step:max
  const rangeStepMatch = t.match(
    /^(-?\d+(?:\.\d+)?):(-?\d+(?:\.\d+)?):(-?\d+(?:\.\d+)?)$/,
  );
  if (rangeStepMatch) {
    return {
      kind: 'range',
      min: Number(rangeStepMatch[1]),
      step: Number(rangeStepMatch[2]),
      max: Number(rangeStepMatch[3]),
    };
  }

  // Range without step: min:max
  const rangeMatch = t.match(/^(-?\d+(?:\.\d+)?):(-?\d+(?:\.\d+)?)$/);
  if (rangeMatch) {
    return {
      kind: 'range',
      min: Number(rangeMatch[1]),
      max: Number(rangeMatch[2]),
    };
  }

  // Free: bare number
  const num = Number(t);
  if (!isNaN(num) && t !== '') {
    return { kind: 'free', defaultValue: num };
  }

  return null;
}

/**
 * Attempt to parse a nested numeric array literal and a matching polygon
 * constraint comment into a polygon pad definition.
 *
 * `valueStr` example: `[[0,0], [10,0], [10,10], [0,10]]`
 * `comment`  example: `[[!0,!0], [0:20, !0], [0:20, 0:20], [!0, 0:20]]`
 *
 * Returns `{ flatPoints, config }` on success, or `null` if the value or
 * comment does not match the expected polygon structure.
 */
export function parsePolygonPad(
  valueStr: string,
  comment: string,
): { flatPoints: number[]; config: PolygonPadConfig } | null {
  // --- Parse the value: [[x0,y0], [x1,y1], ...] ---
  const innerArrayPattern =
    /\[\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*\]/g;
  const points: [number, number][] = [];
  let pointMatch: RegExpExecArray | null;
  while ((pointMatch = innerArrayPattern.exec(valueStr)) !== null) {
    points.push([Number(pointMatch[1]), Number(pointMatch[2])]);
  }
  if (points.length < 2) return null;

  // --- Parse the comment constraint array: [[xc,yc], ...] ---
  const constraintPattern = /\[\s*([^[\]]+?)\s*\]/g;
  const trimmedComment = comment.trim();
  if (!trimmedComment.startsWith('[') || !trimmedComment.endsWith(']')) {
    // No constraint comment — generate 'free' constraints from default values
    const pointConstraints: PolygonPointConstraint[] = points.map(([x, y]) => [
      { kind: 'free', defaultValue: x },
      { kind: 'free', defaultValue: y },
    ]);
    return {
      flatPoints: points.flat(),
      config: { pointConstraints },
    };
  }

  // Extract inner [x_constraint, y_constraint] pairs from the comment.
  const innerComment = trimmedComment.slice(1, -1).trim();
  const constraintPairs: PolygonPointConstraint[] = [];
  let cm: RegExpExecArray | null;
  while ((cm = constraintPattern.exec(innerComment)) !== null) {
    const inner = cm[1];
    const parts = inner.split(',').map((s) => s.trim());
    if (parts.length !== 2) return null;
    const xc = parsePolygonAxisConstraint(parts[0]);
    const yc = parsePolygonAxisConstraint(parts[1]);
    if (!xc || !yc) return null;
    constraintPairs.push([xc, yc]);
  }

  // Number of constraints must match number of points.
  if (constraintPairs.length !== points.length) {
    // Mismatch — fall back to free constraints
    const pointConstraints: PolygonPointConstraint[] = points.map(([x, y]) => [
      { kind: 'free', defaultValue: x },
      { kind: 'free', defaultValue: y },
    ]);
    return {
      flatPoints: points.flat(),
      config: { pointConstraints },
    };
  }

  return {
    flatPoints: points.flat(),
    config: { pointConstraints: constraintPairs },
  };
}

/**
 * Detects polygon pad syntax: a nested array of 2-element numeric sub-arrays,
 * e.g. `[[0,0], [10,0], [10,10], [0,10]]`, with an optional constraint comment.
 *
 * Returns null if the value does not match the polygon structure.
 */
export function parsePolygonPadType(
  valueStr: string,
  comment: string,
): PolygonPadParseResult | null {
  const polygonParsed = parsePolygonPad(valueStr, comment);
  if (!polygonParsed) return null;
  return {
    type: 'polygonpad',
    value: polygonParsed.flatPoints,
    polygonPadConfig: polygonParsed.config,
  };
}
